<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Label from 'dashboard/components-next/label/Label.vue';

const inboxId = defineModel('inboxId', {
  type: [String, Number],
  required: true,
});
const template = defineModel('template', { type: Object, default: null });

const { t } = useI18n();

const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');
// getFilteredWhatsAppTemplates already applies isSendableTemplate from
// @chatwoot/utils (approved, non-AUTHENTICATION, no CSAT, no unsupported
// component types) - the exact same filter the composer/reply box uses, so
// the library never shows a template that isn't actually sendable.
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const searchQuery = ref('');

const inboxOptions = computed(() =>
  (inboxes.value || []).map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const templates = computed(() => {
  if (!inboxId.value) return [];
  return getFilteredWhatsAppTemplates.value(inboxId.value) || [];
});

const filteredTemplates = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return templates.value;
  return templates.value.filter(tpl => tpl.name.toLowerCase().includes(query));
});

const friendlyName = name =>
  name.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());

const isSelected = tpl =>
  template.value?.name === tpl.name &&
  template.value?.language === tpl.language;

const selectTemplate = tpl => {
  template.value = tpl;
};

// Selecting a new inbox invalidates whatever template was chosen for the
// previous one.
watch(inboxId, () => {
  template.value = null;
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm text-n-slate-11">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE.STEP_LABEL') }}
    </p>

    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE.INBOX_LABEL') }}
      </label>
      <ComboBox
        v-model="inboxId"
        :options="inboxOptions"
        :placeholder="t('CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE.INBOX_PLACEHOLDER')"
      />
    </div>

    <template v-if="inboxId">
      <Input
        v-model="searchQuery"
        :placeholder="t('CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE.SEARCH_PLACEHOLDER')"
      />

      <div class="flex flex-col gap-2 overflow-y-auto max-h-72">
        <button
          v-for="tpl in filteredTemplates"
          :key="`${tpl.name}-${tpl.language}`"
          type="button"
          class="flex flex-col gap-1 p-3 text-left transition-colors border rounded-lg"
          :class="
            isSelected(tpl)
              ? 'border-n-blue-9 bg-n-blue-2'
              : 'border-n-weak hover:bg-n-slate-3'
          "
          @click="selectTemplate(tpl)"
        >
          <div class="flex items-center justify-between gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ friendlyName(tpl.name) }}
            </span>
            <Label
              :label="tpl.status"
              color="teal"
              compact
              class="capitalize"
            />
          </div>
          <div class="flex items-center gap-2 text-xs text-n-slate-11">
            <span>{{ tpl.language }}</span>
            <span v-if="tpl.category">&middot; {{ tpl.category }}</span>
          </div>
        </button>

        <p
          v-if="!filteredTemplates.length"
          class="p-3 text-sm text-center text-n-slate-11"
        >
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.TEMPLATE.NO_TEMPLATES') }}
        </p>
      </div>
    </template>
  </div>
</template>
